import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import PublicContentPageShell from '@components/layout/PublicContentPageShell';
import { brandColors } from '@config/brandTokens';
import {
  DEFAULT_SUBSCRIPTION_OFFERS,
  earlyBirdPrice,
  fetchSubscriptionOffers,
  formatUsdYearly,
  type SubscriptionOffer,
} from '@/content/subscriptionPricing';

const SubscriptionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState<SubscriptionOffer[]>(DEFAULT_SUBSCRIPTION_OFFERS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchSubscriptionOffers();
      if (!cancelled) {
        setOffers(data);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const playerOffer = offers.find((o) => o.id === 'players_parents');

  return (
    <PublicContentPageShell
      title="Subscriptions"
      subtitle="Yearly plans for players & parents, academies, schools, and corporate. Register free, then purchase a plan when you are ready."
      maxWidth="lg"
    >
      <Box
        sx={{
          mb: 2,
          px: 2,
          py: 1.25,
          borderRadius: 999,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: brandColors.ice,
          border: '1px solid #e2e8f0',
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: brandColors.navy }}>
          Billing
        </Typography>
        <Chip
          size="small"
          label="Yearly"
          sx={{
            fontWeight: 800,
            bgcolor: brandColors.navy,
            color: brandColors.white,
            height: 26,
          }}
        />
      </Box>

      {playerOffer?.offerHeadline && (
        <Typography
          sx={{
            mb: 0.5,
            fontWeight: 800,
            color: brandColors.navy,
            fontFamily: '"Sora", Arial, sans-serif',
            fontSize: { xs: '1.05rem', md: '1.2rem' },
          }}
        >
          {playerOffer.offerHeadline}
        </Typography>
      )}
      {playerOffer?.termsNote && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2.5 }}>
          {playerOffer.termsNote}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2.5} alignItems="stretch">
          {offers.map((tier) => {
            const isPlayer = tier.id === 'players_parents' && !tier.customPricing && tier.yearlyUsd != null;
            const early =
              isPlayer && tier.earlyBirdEnabled
                ? earlyBirdPrice(tier.yearlyUsd!, tier.earlyBirdPercent)
                : null;
            return (
              <Grid item xs={12} sm={6} md={offers.length >= 4 ? 3 : 4} key={tier.id}>
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 3,
                    bgcolor: brandColors.white,
                    border: tier.highlight ? `2px solid ${brandColors.navy}` : '1px solid #e2e8f0',
                    boxShadow: tier.highlight
                      ? '0 16px 40px rgba(17, 23, 57, 0.12)'
                      : '0 8px 24px rgba(15, 23, 42, 0.05)',
                    position: 'relative',
                  }}
                >
                  {tier.highlight && tier.earlyBirdEnabled && (
                    <Chip
                      label={`${tier.earlyBirdPercent}% early bird`}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 14,
                        right: 14,
                        fontWeight: 800,
                        bgcolor: brandColors.orange,
                        color: brandColors.white,
                      }}
                    />
                  )}

                  <Typography
                    sx={{
                      fontFamily: '"Sora", Arial, sans-serif',
                      fontWeight: 800,
                      fontSize: '1.25rem',
                      color: brandColors.navy,
                      pr: tier.highlight ? 12 : 0,
                    }}
                  >
                    {tier.id === 'schools' ? 'Schools/Colleges' : tier.name}
                  </Typography>
                  <Typography sx={{ color: brandColors.slate, fontSize: '0.9rem', mt: 0.35, mb: 1 }}>
                    {tier.audience}
                  </Typography>

                  {(tier.sports || []).length > 0 && (
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                      {tier.sports.map((sport) => (
                        <Chip
                          key={sport}
                          size="small"
                          label={sport}
                          sx={{ fontWeight: 700, bgcolor: brandColors.ice, color: brandColors.navy }}
                        />
                      ))}
                    </Stack>
                  )}

                  {isPlayer ? (
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="baseline">
                        <Typography
                          sx={{
                            fontFamily: '"Sora", Arial, sans-serif',
                            fontWeight: 900,
                            fontSize: '2.4rem',
                            color: brandColors.navy,
                            lineHeight: 1,
                          }}
                        >
                          {formatUsdYearly(early ?? tier.yearlyUsd!)}
                        </Typography>
                        <Typography sx={{ color: brandColors.slate, fontWeight: 600 }}>/ year</Typography>
                      </Stack>
                      {early != null && (
                        <Typography
                          sx={{
                            mt: 0.75,
                            color: brandColors.slate,
                            fontWeight: 600,
                            fontSize: '0.85rem',
                          }}
                        >
                          Early bird {tier.earlyBirdPercent}% off · usually {formatUsdYearly(tier.yearlyUsd!)} / year
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        sx={{
                          fontFamily: '"Sora", Arial, sans-serif',
                          fontWeight: 900,
                          fontSize: '2rem',
                          color: brandColors.navy,
                          lineHeight: 1.1,
                        }}
                      >
                        Custom
                      </Typography>
                      {tier.earlyBirdEnabled && tier.earlyBirdPercent > 0 ? (
                        <Typography sx={{ mt: 0.75, fontWeight: 700, color: brandColors.orange, fontSize: '0.95rem' }}>
                          {tier.earlyBirdPercent}% early bird
                          {tier.earlyBirdLimit != null ? ` · first ${tier.earlyBirdLimit}` : ''}
                        </Typography>
                      ) : null}
                      <Typography sx={{ mt: 0.75, color: brandColors.slate, fontSize: '0.9rem' }}>
                        Offer varies by size and sports — contact us for a yearly quote.
                      </Typography>
                    </Box>
                  )}

                  <Typography
                    sx={{
                      fontSize: '0.78rem',
                      fontWeight: 800,
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                      color: brandColors.slate,
                      mb: 1,
                    }}
                  >
                    {tier.includesNote || 'Includes'}
                  </Typography>

                  <Stack spacing={1} sx={{ flex: 1, mb: 2.5 }}>
                    {tier.features.map((feature) => (
                      <Stack key={feature} direction="row" spacing={1} alignItems="flex-start">
                        <CheckIcon sx={{ fontSize: 18, color: brandColors.blue, mt: '2px' }} />
                        <Typography sx={{ fontSize: '0.92rem', color: '#1e293b', lineHeight: 1.4 }}>
                          {feature}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>

                  <Button
                    fullWidth
                    variant={tier.highlight ? 'contained' : 'outlined'}
                    onClick={() => {
                      if (tier.customPricing) {
                        navigate(
                          tier.ctaPath?.startsWith('/subscriptions/contact')
                            ? tier.ctaPath
                            : `/subscriptions/contact?plan=${tier.id}`,
                        );
                        return;
                      }
                      navigate(`/subscriptions/purchase?plan=${tier.id}`);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      py: 1.1,
                      bgcolor: tier.highlight ? brandColors.navy : undefined,
                      borderColor: brandColors.navy,
                      color: tier.highlight ? brandColors.white : brandColors.navy,
                      '&:hover': {
                        bgcolor: tier.highlight ? '#0a0f28' : 'rgba(17,23,57,0.06)',
                        borderColor: brandColors.navy,
                      },
                    }}
                  >
                    {tier.customPricing ? tier.ctaLabel || 'Contact sales' : 'Purchase yearly plan'}
                  </Button>
                  {!tier.customPricing && (
                    <Button
                      fullWidth
                      onClick={() => navigate(tier.ctaPath || '/register/player')}
                      sx={{
                        mt: 0.75,
                        textTransform: 'none',
                        fontWeight: 700,
                        color: brandColors.blue,
                      }}
                    >
                      Register free first
                    </Button>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3, maxWidth: 720, lineHeight: 1.55 }}>
        {playerOffer?.termsNote || '*Terms & conditions apply.'} Player / Parent plan covers Football,
        Cricket and Badminton on one public profile, with 4 assessments per year (unused assessments carry
        forward). Register stays free; use Purchase yearly plan when you are ready. Academies, Schools/Colleges,
        and Corporate receive tailored yearly packages after a short discovery call — use Contact sales, not
        the Support form.
      </Typography>
    </PublicContentPageShell>
  );
};

export default SubscriptionsPage;
